import { createSelector } from "reselect"
import { chain, get, map, isFinite, capitalize } from "lodash"
import { getContentSets } from "selectors/contentSets"
import { getFactorName } from "helpers/factors"
import { getClassifications } from "modules/smart-trade-rule-search/selectors"
import { makeRuleValueGetter } from "modules/smart-trade-rule-search/helpers"
import { getModelFactors } from "selectors/modelMetadata"
import { getConstraintRule } from "./constraints"
import { rulesAreEqual, rulesAreEmpty, hasError, allRulesHaveNoValue, computeMinMax, isCurrency, isCountry, isClassification } from "../helpers"

const RULES = (longOrShort) => ([{
  id: "basketSize",
  label: "Basket As % Of Trade Size",
  unit: "%",
  validate: (value) => {
    if (!value || !isFinite(value) || value === 0) {
      return "Invalid value."
    }
    if (longOrShort === "LONG_ONLY" && value > 0) {
      return "Invalid value."
    }
    if (longOrShort === "SHORT_ONLY" && value < 0) {
      return "Invalid value."
    }
    return ""
  },
}, {
  id: "maxTradeADV",
  label: "Max Trade As % Of ADV",
  unit: "% ADV",
  validate: (value) => {
    if (value) {
      if (!isFinite(value)) {
        return "Invalid value."
      }

      if (value < 0) {
        return "% ADV must be positive"
      }
    }
  },
}, {
  id: "maxConcentration",
  label: "Max Concentration of Basket Constituents",
  unit: "%",
  validate: (value) => value && !isFinite(value) ? "Invalid value." : "",
},
{
  id: "targetPositions",
  label: "Target # of Positions",
  tooltip: "This rule aims to achieve this target number of positions by minimizing risk while varying the factor and specific risk tradeoff",
  step: 1,
  validate: (value) => value && (!isFinite(value) || value < 1) ? "Invalid value." : "",
}])

const getTradeSize = (_, props) => get(props, "tradeSize")
const getPropsBasketRules = (_, props) => get(props, "basketRules")
const getPropsSmartTradeRules = (_, props) => get(props, "smartTradeRules")
const getRawSmartTradeRules = state => get(state, "modules.smartTradeRuleSearch.smartTradeRules")
const getCountries = state => get(state, "global.data.modelCountries.data")
const getCurrencies = state => get(state, "global.data.modelCurrencies.data")
const getRawBasketRules = state => get(state, "modules.smartTradePanel.basketRules")
const getExperimentConfig = (state) => get(state, "global.data.smartTradeExperimentMeta.data.config.setup")
const getExperimentBasketRules = (state) => get(state, "global.data.smartTradeExperimentMeta.data.config.setup.basketRules")
const getExperimentSmartTradeRules = (state) => get(state, "global.data.smartTradeExperimentMeta.data.config.setup.smartTradeRules")
export const getSmartTradeRulesSearchValue = state => get(state, "modules.smartTradePanel.smartTradeRulesSearchValue")
export const getInstrumentLongOrShort = state => get(state, "global.data.instrumentPortfolioModelMeta.data.type")

const getAppliedBasketRules = createSelector(
  [getExperimentBasketRules, getPropsBasketRules],
  (experimentBasketRules, propsBasketRules) => propsBasketRules || experimentBasketRules,
)

// Applied basket rules + basket rules under modification (not applied)
const getActiveBasketRules = createSelector(
  [getAppliedBasketRules, getRawBasketRules],
  (appliedBasketRules, basketRules) => ({ ...appliedBasketRules, ...basketRules }),
)

const getAppliedSmartTradeRules = createSelector(
  [getExperimentSmartTradeRules, getPropsSmartTradeRules],
  (experimentSmartTradeRules, propsSmartTradeRules) => {
    return propsSmartTradeRules || experimentSmartTradeRules
  },
)

// Applied smart trade rules + smart trade rules under modification (not applied)
const getActiveSmartTradeRules = createSelector(
  [getAppliedSmartTradeRules, getRawSmartTradeRules],
  (appliedSmartTradeRules, smartTradeRules) => smartTradeRules || appliedSmartTradeRules,
)

export const getExperimentTradeSize = createSelector(
  [getExperimentConfig, getTradeSize],
  (experiment, tradeSize) => get(experiment, "tradeSize", tradeSize),
)

export const getComputedOptimizationRules = createSelector(
  [getActiveBasketRules, getActiveSmartTradeRules],
  (activeBasketRules, activeSmartTradeRules) => {
    const smartTradeRules = map(activeSmartTradeRules, getOptimizerRule)
    return ({
      basketRules: activeBasketRules,
      smartTradeRules,
    })
  },
)

// For display and validation purposes
export const getBasketRules = createSelector(
  [getActiveBasketRules, getInstrumentLongOrShort],
  (rulesValues, longOrShort) => map(RULES(longOrShort), (elem) => ({
    ...elem,
    value: rulesValues[elem.id],
    error: elem.validate(rulesValues[elem.id]),
  }),
  ),
)

// For display and validation purposes
export const getSmartTradeRules = createSelector(
  [getActiveSmartTradeRules, getModelFactors, getContentSets, getCurrencies, getCountries, getClassifications],
  (smartTradeRules, factorsMetadata, contentSets, currencies, countries, classifications) => {
    const rules = chain(smartTradeRules)
      .map((savedRule) => {
        const { min, max, error } = computeMinMax(savedRule.min, savedRule.max)
        let rule = {
          ...savedRule,
          error,
          min,
          max,
          unit: "",
          getter: makeRuleValueGetter(savedRule),
        }

        if (savedRule.type.startsWith("factor")) {
          rule.contentSetId = savedRule.contentSetId
          rule.name = getFactorName(savedRule.id, savedRule.contentSetId, factorsMetadata, contentSets)
        } else if (savedRule.type === "constraint") {
          rule = { ...rule, ...getConstraintRule(savedRule) }
        } else if (savedRule.type === "beta") {
          rule.name = `${capitalize(rule.id)} - Beta`
        } else {
          let coll
          switch (true) {
            case isCurrency(savedRule):
              coll = currencies
              break
            case isCountry(savedRule):
              coll = countries
              break
            case isClassification(savedRule):
              coll = classifications[savedRule.classificationId]
              break
          }
          rule.unit = "%"
          rule.name = chain(coll).find(elem => elem.value === rule.id).get("label").value()
        }
        return rule
      })
      .filter(factor => factor)
      .value()
    return rules
  },
)

export const getRulesApplyButtonEnabled = createSelector(
  [getBasketRules, getSmartTradeRules, getActiveBasketRules, getAppliedBasketRules, getActiveSmartTradeRules, getAppliedSmartTradeRules],
  (currentBasketRules, currentSmartTradeRules, activeBasketRules, appliedBasketRules, activeSmartTradeRules, appliedSmartTradeRules) => {
    // if any of these are true, we cant apply changes
    return ![
      rulesAreEqual(activeBasketRules, appliedBasketRules) && rulesAreEqual(activeSmartTradeRules, appliedSmartTradeRules),
      rulesAreEmpty(activeBasketRules, appliedBasketRules) && rulesAreEmpty(activeSmartTradeRules, appliedSmartTradeRules),
      hasError(currentBasketRules),
      hasError(currentSmartTradeRules),
      allRulesHaveNoValue(currentSmartTradeRules) && allRulesHaveNoValue(appliedSmartTradeRules),
    ].some((value) => value)
  },
)

function getOptimizerRule(rule) {
  return {
    ...rule,
    min: isFinite(rule.min) ? parseFloat(rule.min) : null,
    max: isFinite(rule.max) ? parseFloat(rule.max) : null,
  }
}
